import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Building2 } from "lucide-react";
import type { CompanyCostReport } from "@/lib/dashboardCosts";

const formatNumber = (n: number) => new Intl.NumberFormat("es-ES").format(n);
const formatEur = (n: number) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: 2 }).format(n);
const formatCredits = (n: number) =>
  new Intl.NumberFormat("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n);

const CompanyCostTable = ({ report }: { report: CompanyCostReport }) => (
  <Card>
    <CardContent className="p-0">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Empresa</TableHead>
              <TableHead className="text-right">Documentos</TableHead>
              <TableHead className="text-right">Llamadas IA</TableHead>
              <TableHead className="text-right">Coste IA real</TableHead>
              <TableHead className="text-right">Coste nube</TableHead>
              <TableHead className="text-right">Otros costes</TableHead>
              <TableHead className="text-right">Coste total</TableHead>
              <TableHead className="text-right">%</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {report.rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="p-8 text-center text-sm text-muted-foreground">
                  No hay documentos tramitados en este periodo.
                </TableCell>
              </TableRow>
            ) : (
              <>
                {report.rows.map((r) => (
                  <TableRow key={r.companyId}>
                    <TableCell>
                      <span className="inline-flex items-center gap-2">
                        <Building2 className="h-3 w-3 text-muted-foreground" />
                        {r.name}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono">{formatNumber(r.documents)}</TableCell>
                    <TableCell className="text-right font-mono">{formatNumber(r.aiCalls)}</TableCell>
                    <TableCell
                      className="text-right font-mono"
                      title={`${formatCredits(r.aiCredits)} créditos`}
                    >
                      {formatEur(r.aiEurReal)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">
                      {formatEur(r.hostingEur)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">
                      {formatEur(r.otherEur)}
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold">{formatEur(r.totalEur)}</TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">
                      {(r.share * 100).toFixed(1)}%
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-t-2 font-semibold">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right font-mono">{formatNumber(report.totals.documents)}</TableCell>
                  <TableCell className="text-right font-mono">{formatNumber(report.totals.aiCalls)}</TableCell>
                  <TableCell
                    className="text-right font-mono"
                    title={`${formatCredits(report.totals.aiCredits)} créditos`}
                  >
                    {formatEur(report.totals.aiEurReal)}
                  </TableCell>
                  <TableCell className="text-right font-mono">{formatEur(report.totals.hostingEur)}</TableCell>
                  <TableCell className="text-right font-mono">{formatEur(report.totals.otherEur)}</TableCell>
                  <TableCell className="text-right font-mono">{formatEur(report.totals.totalEur)}</TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground">100%</TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </div>
      <p className="border-t px-4 py-3 text-xs text-muted-foreground">
        El coste de IA es el consumo realmente registrado por cada llamada (extracción, comprobación de
        identidad y de IVA, y sugerencia de cuenta). El coste de nube corresponde al hosting y "Otros costes"
        al desarrollo y mantenimiento, ambos prorrateados por actividad.
      </p>

    </CardContent>
  </Card>
);

export default CompanyCostTable;
